// Script API (SCR-2, ARCH §27.5). PURE (ENG-1) — the whitelisted verb surface a
// script may call, over a `ScriptHost` the game host implements on top of its
// CommandSource (D16, `sim/game/commands/core.ts`) plus a read-only world/event view.
//
// Only the verbs below exist; there is no `fetch`/`window`/`import`/timer/`client/`
// verb in the table, so a script simply cannot name one (the parser rejects any
// other call as an unknown function). Every verb type-checks its arguments and a
// `fill`/`setblock` volume against SCR_FILL_MAX, aborting the tick (via ScriptError)
// rather than editing a mega-region. Effects are deterministic given the host.
//
// HOST SLICES (§27.5, M45.4) — "the verb table IS the sandbox", and since §27.5 the
// table is SLICED: `VERB_SLICES` records, for every verb, whether a `'block'` script
// may name it, a `'mob'` script may, or both. `parse.ts` consults it through
// `verbAllowedIn`, so a mob script naming `fill` or `give` is a COMPILE error naming
// the verb and the host kind (E-47 kin) rather than a runtime surprise, and a block
// script naming `self.moveTo`/`mem.*`/`handled` is refused the same way. The three
// block-only host verbs are refused a second time at dispatch (`blockOnly`) for the
// case where a caller compiled a mob source without telling the compiler its kind —
// a forgotten argument must not be a way through the sandbox.
//
// THE MOB SLICE (§27.5/§27.6) is a SECOND, OPTIONAL surface: `VerbCtx.mob`, absent for
// every block dispatch, so `ScriptHost` — which the client, the server and every test
// already implement — is untouched by M45.4. A `self.*`/`mem.*`/`handled` call with no
// mob surface aborts the dispatch, the same convention `rng` already uses: a silent
// no-op would read as "the mob ignored me" instead of "this host has no mob".
//
// NO POSE WRITER (§27.6, binding): `self.moveTo`/`moveAway` write the entity's AI
// scratch — `Entity.wishX`/`wishZ` (MOB-3 steering intent), reached through
// `ScriptMobHost.steering` — and there is deliberately NO verb, and no member of
// `ScriptMobHost`, that writes `pos` or `vel`. That is what keeps the physics contract
// unscriptable, and `tests/script_slice.test.ts` asserts it over the whole table so a
// future pose-writing verb fails there.
//
// The list verbs (`list`/`at`/`len`/`push`, SCR-5 §27.2) are pure and host-free, and
// this module is the ONE place `SCR.LIST_MAX` is enforced: `list(…)` is variadic up
// to that ceiling (the parser rejects more at compile time) and `push` refuses to
// build past it at runtime. Lists are immutable — `push` returns a NEW list.
import { SCR } from '../config';
import { ScriptError } from './lang/lex';
import {
  type Value, type Vec3, type ScriptList, vec3, list, isVec3, isList, valueType, valueToString,
} from './lang/values';
import type { ScriptRng } from './rng';
import type { ScriptHostKind } from './system';
import { isScriptName } from '../engine/saves';

/** Re-exported so `parse.ts` (and the slice tests) take the host kind from the module
 *  that owns the slice table, rather than reaching into the runtime for a type.
 *  `system.ts` remains its definition site — this is an alias, never a second copy. */
export type { ScriptHostKind };

/** The world/effect binding a script runs against. The game host implements this
 *  by delegating to its CommandContext (setBlock/fill/summon/give) and a read-only
 *  world+entity view; a headless test implements it directly. All methods are
 *  deterministic — reads are snapshots, writes are the same edits `/setblock` makes.
 *
 *  UNCHANGED by the M45.4 mob slice: the mob verbs live on `ScriptMobHost` below, so
 *  every existing implementation (client boot, the server's world host, the test
 *  fakes) keeps compiling untouched. */
export interface ScriptHost {
  /** Block name at a world cell (air ⇒ 'air'). */
  getBlock(x: number, y: number, z: number): string;
  /** Set a cell; false if the block name is unknown. */
  setBlock(x: number, y: number, z: number, name: string): boolean;
  /** Fill an inclusive box; returns the number of cells set. Volume is pre-checked
   *  ≤ SCR_FILL_MAX by the API. */
  fill(x1: number, y1: number, z1: number, x2: number, y2: number, z2: number, name: string): number;
  /** Spawn a mob; false if the type is unknown. `script`, when given, is §27.13's
   *  per-instance override attached at spawn (`summon(mob, x, y, z, scriptId)`) — an
   *  identifier the verb layer has already checked. Optional, so a host written before
   *  M47.3 still satisfies the interface; such a host simply drops it. */
  summon(mob: string, x: number, y: number, z: number, script?: string): boolean;
  /** Give the command source `count` of an item; false if unknown. */
  give(item: string, count: number): boolean;
  /** Post a chat/console line (UI-7 surfaced). */
  say(message: string): void;
  /** §27.10's `emit(name, value)`: raise a signal for scripted entities within
   *  `SCR.SIGNAL.RADIUS`. Returns whether it was QUEUED — false means §27.10's depth or
   *  per-tick dispatch bound refused it, and the refusal has been counted (E-46).
   *
   *  **The ORIGIN is supplied by the verb layer, not asked of the host** (`x`/`y`/`z`
   *  come from `selfPos`, the dispatch's own position — a mob's live pose or the cell a
   *  block script sits in). A script therefore cannot raise a signal at arm's length,
   *  which is the same rule §27.5's proximity reads follow; and the host, which is ONE
   *  shared object across every dispatch in a tick, never has to be told which dispatch
   *  is currently running.
   *
   *  **Optional, like those reads and for the same reason (D-TF-197):** making it
   *  required would break `client/boot.ts`, `server/world-host.ts` and every headless
   *  test fake at once. A host without it aborts the verb rather than pretending the
   *  signal went somewhere. */
  emit?(name: string, payload: Value, x: number, y: number, z: number): boolean;
  /** True iff the block at a cell equals `name`. */
  detect(x: number, y: number, z: number, name: string): boolean;
  /** Position of the nearest entity within `radius` of a point, or null if none. */
  nearestEntity(x: number, y: number, z: number, radius: number): Vec3 | null;
  /** Apply one tick of control to the source's aircraft (AVI-3); false if not flying.
   *  `throttle` > 0 spools up, < 0 spools down. */
  setAircraftControl(pitch: number, roll: number, throttle: number): boolean;
  /** §27.5's entity/player reads. **Optional on purpose (D-TF-197):** they arrived with
   *  the mob slice, and making them required would break every existing implementor —
   *  `client/boot.ts`, `server/world-host.ts` and the headless test fakes — none of
   *  which the task that introduced the verbs was allowed to touch. A host that does
   *  not implement one aborts that verb's dispatch (E-30) rather than inventing an
   *  answer, which is the same rule `random`/`chance` follow when no rng is supplied.
   *
   *  Position of the nearest player, or null if none is in range. */
  nearestPlayer?(x: number, y: number, z: number): Vec3 | null;
  /** True iff a player is within `radius` of the point. */
  playerWithin?(x: number, y: number, z: number, radius: number): boolean;
  /** True iff an entity of `kind` is within `radius` of the point. */
  entityWithin?(x: number, y: number, z: number, radius: number, kind: string): boolean;
}

/** The AI scratch a scripted mob steers with — `Entity.wishX`/`wishZ`/`wishJump`
 *  (MOB-3), handed to the verb layer as a MUTABLE view of those three fields and
 *  nothing else. §27.6: "`self.*` steering writes the existing AI scratch …  scripts
 *  never write `pos` or `vel` directly". Passing the scratch itself (rather than a
 *  `moveTo` method on the host) is what makes that checkable: the only fields a verb
 *  can reach are the three named here. */
export interface MobSteering {
  /** Desired horizontal velocity this tick, world space (`Entity.wishX`). */
  wishX: number;
  /** Desired horizontal velocity this tick, world space (`Entity.wishZ`). */
  wishZ: number;
  /** Ask physics to jump. `stepPhysics` converts it to vertical velocity only while
   *  the mob is on the ground, so this is a hop request, never scripted flight. */
  wishJump: boolean;
}

/** One MOB-3 steering step, structurally the `SteerResult` of
 *  `sim/entities/pathfind/steer.ts` (duplicated as a type rather than imported so the
 *  script layer stays a leaf over the entity layer, not a dependent of it). */
export interface MobPath {
  /** Unit horizontal direction to travel (0,0 ⇒ hold: blocked, cliff or arrived). */
  dx: number;
  dz: number;
  /** True when the step needs a hop — already gated on the mob standing on ground by
   *  the host, which is the side that can read `Entity.onGround`. */
  jump: boolean;
}

/** What one entity's script memory may hold (§27.7): number, string or bool. No vec3
 *  and no list — store a position as three numbers, which is what keeps a save's
 *  memory bag a flat record. */
export type MobMemValue = number | string | boolean;

/** The mob half of the verb table (§27.5 `self.*`/`mem.*`/`handled`), implemented by
 *  the entity host over ONE entity for ONE dispatch. Absent from a block dispatch.
 *
 *  Pose is read-only on purpose: `x/y/z` are here because `moveAway`/`faceAt` need the
 *  mob's own position to compute a goal, and there is no setter for them, for `vel`,
 *  or for anything else physics owns (§27.6). Movement leaves through `steering`. */
export interface ScriptMobHost {
  /** The entity's current position (read-only — see the §27.6 note above). */
  readonly x: number;
  readonly y: number;
  readonly z: number;
  /** The entity's AI scratch for this tick: the ONE channel `self.*` steering writes. */
  readonly steering: MobSteering;
  /** MOB-3 greedy steering toward a horizontal goal (`steerToward` + the mob's
   *  `tickSpeed`-scaled walk speed is applied by the verb layer, so a script cannot
   *  outrun its `MobDef.speed`). */
  pathToward(goalX: number, goalZ: number): MobPath;
  /** Walking speed in blocks/tick (`tickSpeed(def)`) — the scale `moveTo` multiplies
   *  the unit direction by, so speed stays a registry stat, never a script argument. */
  moveSpeed(): number;
  /** Face a yaw in RADIANS (`Entity.yaw`). Orientation is neither position nor
   *  velocity, so it is scriptable; physics is not. */
  face(yaw: number): void;
  /** Target an entity id; false if no such entity is visible to this mob. */
  setTarget(id: number): boolean;
  /** Drop the current target. */
  clearTarget(): void;
  /** Strike the current target with the mob's own `attackDamage` on its own cooldown;
   *  false if there is no target, it is out of reach, or the cooldown is unspent. */
  attack(): boolean;
  /** Damage / heal the mob itself, clamped by the host to its `maxHealth`. */
  hurt(amount: number): void;
  heal(amount: number): void;
  /** Remove the mob (MOB-4 despawn path — not a death, so it drops no loot). */
  despawn(): void;
  /** Read one memory key, or undefined when unset (`mem.get`'s default then applies). */
  memGet(key: string): MobMemValue | undefined;
  /** Write one memory key; false when the bag is full (`SCR.MEM.KEYS`), which aborts
   *  the dispatch and leaves stored memory intact (§27.7, E-43). */
  memSet(key: string, value: MobMemValue): boolean;
  /** Suppress the archetype for THIS dispatch only (§27.6 fall-through, never sticky). */
  handled(): void;
}

interface Sig { min: number; max: number }
const fix = (n: number): Sig => ({ min: n, max: n });

/** Every callable name (dotted for namespaced verbs). The parser validates calls
 *  against exactly this table — anything else is an "unknown function" compile
 *  error, which is how forbidden identifiers (`fetch`, `window`, `import`) are kept
 *  out of the grammar without a blocklist. Which SLICE each name belongs to is
 *  `VERB_SLICES` below; the two tables are key-for-key identical (asserted). */
export const VERB_SIGNATURES: Readonly<Record<string, Sig>> = {
  // World / effect verbs.
  getblock: fix(3),
  setblock: fix(4),
  fill: fix(7),
  // §27.13: an optional 5th argument names the spawned entity's own script.
  summon: { min: 4, max: 5 },
  give: fix(2),
  say: fix(1),
  // §27.10's signal verb: `emit(name, value)` — exactly two arguments, and the second
  // is ONE value. A list payload is refused at compile time (D-TF-105).
  emit: fix(2),
  detect: fix(4),
  nearestEntity: fix(3),
  // §27.5 read row — available to both slices (a block may ask who is nearby too).
  nearestPlayer: fix(0),
  playerWithin: fix(1),
  entityWithin: fix(2),
  'aircraft.setControl': fix(3),
  // The mob slice (§27.5/§27.6, M45.4). `moveTo`/`moveAway`/`faceAt` take a point
  // either as one vec3 or as x, y, z — 2 arguments is neither, and is refused at the
  // call (the {min,max} form cannot express "1 or 3").
  'self.moveTo': { min: 1, max: 3 },
  'self.moveAway': { min: 1, max: 3 },
  'self.face': fix(1),
  'self.faceAt': { min: 1, max: 3 },
  'self.jump': fix(0),
  'self.setTarget': fix(1),
  'self.clearTarget': fix(0),
  'self.attack': fix(0),
  'self.hurt': fix(1),
  'self.heal': fix(1),
  'self.despawn': fix(0),
  // Per-entity memory (§27.7). `mem.get(key, default)` — the default is required, so
  // a read of an unset key is never a hole in an expression.
  'mem.get': fix(2),
  'mem.set': fix(2),
  // Fall-through control (§27.6).
  handled: fix(0),
  // Lists (SCR-5) — immutable values; `push` returns a new one. `list` is the only
  // variadic verb: 0…SCR.LIST_MAX elements, so an over-long literal is a *compile*
  // error (the parser checks the signature) and a runtime `push` is checked below.
  list: { min: 0, max: SCR.LIST_MAX },
  at: fix(2),
  len: fix(1),
  push: fix(2),
  // Seeded randomness (§27.8) — pure given the per-dispatch rng in VerbCtx.
  random: fix(2),
  chance: fix(1),
  // Pure builtins (no host).
  vec3: fix(3),
  distance: fix(2),
  abs: fix(1),
  floor: fix(1),
  ceil: fix(1),
  round: fix(1),
  sqrt: fix(1),
  sign: fix(1),
  min: fix(2),
  max: fix(2),
  clamp: fix(3),
};

/** Which host kinds may NAME a verb: `'both'`, `'block'`-only or `'mob'`-only.
 *
 *  Transcribed from ARCH §27.5's table (= SCRIPTING §5.3), which is BINDING — one row
 *  per §27.5 row, cited. `ScriptHostKind = 'block' | 'mob'` (§27.5), so `'both'` is
 *  the ✅/✅ rows, `'block'` the ✅/❌ rows and `'mob'` the ❌/✅ rows. Every key in
 *  `VERB_SIGNATURES` appears here and nothing else does (asserted in
 *  `tests/script_slice.test.ts`), so a verb added without a slice fails loudly rather
 *  than defaulting into both sandboxes. */
export type VerbSlice = 'both' | 'block' | 'mob';

export const VERB_SLICES: Readonly<Record<string, VerbSlice>> = {
  // §27.5 "getblock, detect, nearestEntity, nearestPlayer, playerWithin, entityWithin
  // | ✅ | ✅ | world.read" — reads are cheap and harmless.
  getblock: 'both',
  detect: 'both',
  nearestEntity: 'both',
  // The same §27.5 read row. These three measure from the dispatch's own position
  // (`VerbCtx.self`) and take no coordinates, and their `ScriptHost` methods are
  // OPTIONAL — a host without one refuses the verb (D-TF-197).
  nearestPlayer: 'both',
  playerWithin: 'both',
  entityWithin: 'both',
  // §27.5 "setblock | ✅ | ✅ ≤ SCR.MOB.SETBLOCK_MAX cells/dispatch | world.write" —
  // the mob ceiling is a per-dispatch running total, charged in `chargeSetblock`.
  setblock: 'both',
  // §27.5 "fill | ✅ ≤ SCR.FILL_MAX | ❌ compile error | world.write" — "70 mobs ×
  // 4096 cells is world deletion" (SCRIPTING §5.3).
  fill: 'block',
  // §27.5 "summon | ✅ | ✅ under CASCADE.SPAWN_BUDGET | entity.spawn". The mob spawn
  // budget is M46.4's; the SLICE is both, today.
  summon: 'both',
  // §27.5 "give | ❌ compile error for a mob | player.give" — "item duplication from a
  // mob loop" (SCRIPTING §5.3).
  give: 'block',
  // §27.5 "say | ✅ | ✅ rate-limited (§27.8) | chat". The mob rate limit is M45.5's.
  say: 'both',
  // §27.5 "emit | ✅ | ✅ | signal" — a block and a mob may both raise a signal; what
  // RECEIVES one is always an entity (§27.10), which is a fact about delivery, not
  // about who is allowed to emit.
  emit: 'both',
  // §27.5 "aircraft.setControl | ✅ | ❌ | aircraft" — AVI-3 is a block/player
  // affordance, so there is nothing for a mob to control.
  'aircraft.setControl': 'block',
  // §27.5 "self.moveTo/moveAway/face/faceAt/jump/setTarget/clearTarget/attack/hurt/
  // heal/despawn | ❌ | ✅ | self" — "there is no 'self' to steer on a block".
  'self.moveTo': 'mob',
  'self.moveAway': 'mob',
  'self.face': 'mob',
  'self.faceAt': 'mob',
  'self.jump': 'mob',
  'self.setTarget': 'mob',
  'self.clearTarget': 'mob',
  'self.attack': 'mob',
  'self.hurt': 'mob',
  'self.heal': 'mob',
  'self.despawn': 'mob',
  // §27.5 "mem.get/set | ❌ | ✅ | —" — "block state already persists as block-entity
  // data" (SCRIPTING §5.3).
  'mem.get': 'mob',
  'mem.set': 'mob',
  // §27.5 "handled() | ❌ | ✅ | —" — "nothing to fall through to on a block".
  handled: 'mob',
  // §27.5 "vec3,abs,floor,ceil,round,sqrt,sign,min,max,clamp,list,at,len,push,
  // distance,random,chance | ✅ | ✅ | —" — deterministic, no host.
  list: 'both',
  at: 'both',
  len: 'both',
  push: 'both',
  random: 'both',
  chance: 'both',
  vec3: 'both',
  distance: 'both',
  abs: 'both',
  floor: 'both',
  ceil: 'both',
  round: 'both',
  sqrt: 'both',
  sign: 'both',
  min: 'both',
  max: 'both',
  clamp: 'both',
};

/** May a `host`-kind script name `verb`? An UNKNOWN name answers true: it is not this
 *  function's error to report, and parse.ts's "unknown function '…'" is the message
 *  that should survive (a name outside the table is outside every slice anyway). */
export function verbAllowedIn(verb: string, host: ScriptHostKind): boolean {
  const slice = VERB_SLICES[verb];
  return slice === undefined || slice === 'both' || slice === host;
}

/** Read-only property namespaces (validated by the parser for a friendly error;
 *  vec3 `.x/.y/.z` on a value is checked at runtime). */
export const NAMESPACE_PROPS: Readonly<Record<string, ReadonlySet<string>>> = {
  self: new Set(['x', 'y', 'z', 'pos', 'powered']),
  // `amount`/`source`/`payload` are M46's per-hook payloads (§27.6's table, §27.9's
  // block hooks). They are NAMEABLE on every hook — the parser cannot know which hook a
  // read sits in, since top-level statements run on every dispatch — and the VM aborts
  // at runtime when the firing hook does not carry the one that was read.
  event: new Set(['name', 'power', 'amount', 'source', 'payload']),
};

/** Per-dispatch running totals the verb layer must bound itself.
 *
 *  §27.5 caps a mob's `setblock` at `SCR.MOB.SETBLOCK_MAX` **cells per dispatch** — a
 *  RUNNING TOTAL, not a per-call check: four one-cell calls are the ceiling and the
 *  fifth aborts. The count therefore lives on the dispatch's ctx (the VM builds one
 *  `VerbCtx` per dispatch and throws it away). A module-level counter would leak
 *  between entities and between worlds and make the abort depend on dispatch order —
 *  i.e. it would be a determinism bug, not just untidy. */
export interface VerbTally {
  /** Cells this dispatch has already asked `setblock` to write. */
  setblocks: number;
}

export interface VerbCtx {
  host: ScriptHost;
  /** SCR_FILL_MAX — the largest volume a single setblock/fill may touch. */
  fillMax: number;
  /** SCR_ENTITY_RANGE — search radius for nearestEntity. */
  entityRange: number;
  /** SCR.LIST_MAX — longest list `list`/`push` may build (defaults to the config
   *  value when a caller doesn't override it, so older ctx literals still work). */
  listMax?: number;
  /** §27.10's `SCR.CASCADE.SPAWN_BUDGET` charger: returns false once this TICK's
   *  script-caused spawns are spent, and `summon` then refuses.
   *
   *  A closure rather than a number because the budget is **per tick and shared across
   *  every script**, while a `VerbCtx` is per dispatch — a count stored here would
   *  give each dispatch its own eight, which is the fork bomb the budget exists to
   *  stop. Absent ⇒ unbudgeted, which is the pre-M46.4 behaviour and what the pure
   *  verb-table tests want. */
  spawn?: () => boolean;
  /** The per-dispatch seeded RNG behind `random`/`chance` (§27.8), built by the
   *  caller as `scriptRng(entityScriptSeed(mobSeed, entityId), tick)` and NEVER
   *  stored. Absent ⇒ those two verbs abort the dispatch rather than fall back to
   *  an unseeded source: a silent default would be a determinism hole that only
   *  surfaces later as a replay divergence. */
  rng?: ScriptRng;
  /** Where this dispatch is happening — the block's cell or the mob's position. The
   *  proximity reads (`nearestPlayer`/`playerWithin`/`entityWithin`) measure from HERE
   *  and take no coordinates, so a script cannot probe the world at arm's length. */
  self?: Vec3;
  /** The mob this dispatch is FOR (§27.5's mob slice). Absent ⇒ a block dispatch:
   *  `self.*`/`mem.*`/`handled` abort, and the three block-only host verbs are
   *  allowed. Present ⇒ a mob dispatch: those verbs work, `setblock` is charged
   *  against `setblockMax`, and `fill`/`give`/`aircraft.setControl` are refused. */
  mob?: ScriptMobHost;
  /** SCR.MOB.SETBLOCK_MAX — cells one MOB dispatch may set in total (§27.5). */
  setblockMax?: number;
  /** This dispatch's running totals; created on first use, so a caller that omits it
   *  is still bounded (absence must never read as "unlimited"). */
  tally?: VerbTally;
}

/** Dispatch a whitelisted verb. Arguments are already evaluated; this validates
 *  their types + limits and calls the host. Throws ScriptError on any misuse —
 *  the VM catches it and aborts just this tick (E-30), never the tick loop. */
export function callVerb(name: string, args: Value[], ctx: VerbCtx): Value {
  const { host, fillMax } = ctx;
  const listMax = ctx.listMax ?? SCR.LIST_MAX;
  switch (name) {
    case 'getblock': return host.getBlock(int(args[0], name), int(args[1], name), int(args[2], name));
    case 'setblock': {
      // §27.5's mob ceiling, charged BEFORE the edit and per attempt (a refused name
      // still consumed one of the four cells the author was allowed to aim at).
      if (ctx.mob) chargeSetblock(ctx, name);
      return host.setBlock(int(args[0], name), int(args[1], name), int(args[2], name), str(args[3], name));
    }
    case 'fill': {
      blockOnly(ctx, name);
      const [x1, y1, z1, x2, y2, z2] = [int(args[0], name), int(args[1], name), int(args[2], name), int(args[3], name), int(args[4], name), int(args[5], name)];
      const vol = (Math.abs(x2 - x1) + 1) * (Math.abs(y2 - y1) + 1) * (Math.abs(z2 - z1) + 1);
      if (vol > fillMax) throw new ScriptError(`fill volume ${vol} exceeds the limit of ${fillMax}`);
      return host.fill(x1, y1, z1, x2, y2, z2, str(args[6], name));
    }
    case 'summon': {
      // §27.10's per-tick spawn budget. Charged on the ATTEMPT, before the host is
      // asked: a script hammering `summon` at a category that is already full would
      // otherwise spin for free, and the budget exists to bound the work as much as the
      // population. A refusal is `false`, not an abort — the same answer the host gives
      // for an unknown mob, so a script already has to handle it.
      // §27.13: `summon(mob, x, y, z, scriptId)` attaches a per-instance override. The
      // id must be a script NAME (the identifier rule `MobDef.script` uses); anything
      // else aborts before the spawn is charged, since it is a bug in the script, not a
      // full category. Whether the name RESOLVES is the entity's problem later, and
      // E-50 makes that a fallback, never a failure.
      const script = args.length > 4 ? str(args[4], name) : undefined;
      if (script !== undefined && !isScriptName(script)) {
        throw new ScriptError(`summon: '${script}' is not a script name`);
      }
      if (ctx.spawn && !ctx.spawn()) return false;
      const [mob, x, y, z] = [str(args[0], name), int(args[1], name), int(args[2], name), int(args[3], name)];
      return script === undefined ? host.summon(mob, x, y, z) : host.summon(mob, x, y, z, script);
    }
    case 'give': {
      blockOnly(ctx, name);
      return host.give(str(args[0], name), Math.max(1, int(args[1], name)));
    }
    case 'say': host.say(valueToString(args[0]!)); return true;
    case 'emit': {
      const payload = args[1]!;
      // The compile-time gate in `parse.ts` catches a literal `list(…)`/`push(…)`
      // payload. This is the runtime backstop for the cases it cannot see statically —
      // a list arriving through a `fn` parameter or an `at()` of a list of lists.
      // §27.10 is explicit: ONE value, "not a list, not a bag" (D-TF-105).
      if (isList(payload)) {
        throw new ScriptError(`${name}: the payload must be a single value, not a list`);
      }
      // Optional host (see `ScriptHost.emit`): no implementation ⇒ abort, never a
      // silent success that reports a signal nobody could have received.
      if (!host.emit) throw new ScriptError(`${name}: this host cannot raise signals`);
      // The origin is the dispatch's own position (see `ScriptHost.emit`), so a mob
      // measures from its LIVE pose — steering earlier in this same dispatch may have
      // moved it — and a block from its cell.
      const at = selfPos(ctx, name);
      return host.emit(str(args[0], name), payload, at.x, at.y, at.z);
    }
    case 'detect': return host.detect(int(args[0], name), int(args[1], name), int(args[2], name), str(args[3], name));
    case 'nearestEntity': {
      const [x, y, z] = [int(args[0], name), int(args[1], name), int(args[2], name)];
      // No entity → the query point, a harmless target (the value system has no null).
      return host.nearestEntity(x, y, z, ctx.entityRange) ?? vec3(x, y, z);
    }
    case 'aircraft.setControl': {
      blockOnly(ctx, name);
      return host.setAircraftControl(num(args[0], name), num(args[1], name), num(args[2], name));
    }
    case 'nearestPlayer': {
      const at = selfPos(ctx, name);
      return hostRead(host.nearestPlayer, host, name)(at.x, at.y, at.z) ?? vec3(at.x, at.y, at.z);
    }
    case 'playerWithin': {
      const at = selfPos(ctx, name);
      return hostRead(host.playerWithin, host, name)(at.x, at.y, at.z, num(args[0], name));
    }
    case 'entityWithin': {
      const at = selfPos(ctx, name);
      return hostRead(host.entityWithin, host, name)(at.x, at.y, at.z, num(args[0], name), str(args[1], name));
    }
    // ── The mob slice (§27.5/§27.6): steering writes wishX/wishZ, never pos/vel ──
    case 'self.moveTo': return steer(ctx, name, args, false);
    case 'self.moveAway': return steer(ctx, name, args, true);
    case 'self.face': { mobOf(ctx, name).face(num(args[0], name)); return true; }
    case 'self.faceAt': {
      const mob = mobOf(ctx, name);
      const at = point(args, name);
      mob.face(yawToward(mob, at.x, at.z));
      return true;
    }
    case 'self.jump': { mobOf(ctx, name).steering.wishJump = true; return true; }
    case 'self.setTarget': return mobOf(ctx, name).setTarget(int(args[0], name));
    case 'self.clearTarget': { mobOf(ctx, name).clearTarget(); return true; }
    case 'self.attack': return mobOf(ctx, name).attack();
    case 'self.hurt': { mobOf(ctx, name).hurt(amount(args[0], name)); return true; }
    case 'self.heal': { mobOf(ctx, name).heal(amount(args[0], name)); return true; }
    case 'self.despawn': { mobOf(ctx, name).despawn(); return true; }
    // Per-entity memory (§27.7). A read of an unset key yields the caller's default.
    case 'mem.get': return mobOf(ctx, name).memGet(memKey(args[0], name)) ?? val(args[1], name);
    case 'mem.set': return memSet(ctx, name, args);
    case 'handled': { mobOf(ctx, name).handled(); return true; }
    // Lists (SCR-5) — pure, immutable, length-bounded.
    case 'list': return bounded(args, listMax);
    case 'at': {
      const l = lst(args[0], name);
      const i = int(args[1], name);
      if (i < 0 || i >= l.items.length) {
        throw new ScriptError(`at: index ${i} is out of range for a list of length ${l.items.length}`);
      }
      return l.items[i]!;
    }
    case 'len': return lst(args[0], name).items.length;
    case 'push': return bounded([...lst(args[0], name).items, val(args[1], name)], listMax);
    // Seeded randomness (§27.8).
    case 'random': return rngOf(ctx, name).random(num(args[0], name), num(args[1], name));
    case 'chance': return rngOf(ctx, name).chance(num(args[0], name));
    // Pure builtins.
    case 'vec3': return vec3(num(args[0], name), num(args[1], name), num(args[2], name));
    case 'distance': { const a = vecArg(args[0], name), b = vecArg(args[1], name); return Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z); }
    case 'abs': return Math.abs(num(args[0], name));
    case 'floor': return Math.floor(num(args[0], name));
    case 'ceil': return Math.ceil(num(args[0], name));
    case 'round': return Math.round(num(args[0], name));
    case 'sqrt': return Math.sqrt(Math.max(0, num(args[0], name)));
    case 'sign': return Math.sign(num(args[0], name));
    case 'min': return Math.min(num(args[0], name), num(args[1], name));
    case 'max': return Math.max(num(args[0], name), num(args[1], name));
    case 'clamp': { const v = num(args[0], name), lo = num(args[1], name), hi = num(args[2], name); return Math.min(hi, Math.max(lo, v)); }
    default: throw new ScriptError(`unknown function '${name}'`);
  }
}

/** This dispatch's seeded RNG, or an abort. A dispatch that never calls `random`
 *  or `chance` needs no rng, so this is checked at the call, not at construction. */
function rngOf(ctx: VerbCtx, verb: string): ScriptRng {
  if (!ctx.rng) throw new ScriptError(`${verb}: no seeded rng for this dispatch`);
  return ctx.rng;
}

// ── The mob slice's own guards (§27.5) ─────────────────────────────────────────

/** This dispatch's mob, or an abort. Checked at the call for the same reason `rngOf`
 *  is: a block dispatch legitimately has no mob, and §27.5 already refuses these
 *  verbs in a block source at COMPILE time, so reaching here means the source was
 *  compiled without its host kind. Aborting says so; a silent no-op would not. */
function mobOf(ctx: VerbCtx, verb: string): ScriptMobHost {
  if (!ctx.mob) throw new ScriptError(`${verb}: not available to a block script (ARCH §27.5 host slice)`);
  return ctx.mob;
}

/** The mirror of `mobOf` for §27.5's three block-only host verbs. `compile(src,'mob')`
 *  already refuses them, so this only fires when a caller compiled a mob source
 *  without passing its host kind — belt and braces, because "the verb table IS the
 *  sandbox" has to hold even when a call site forgets an argument. */
function blockOnly(ctx: VerbCtx, verb: string): void {
  if (ctx.mob) throw new ScriptError(`${verb}: not available to a mob script (ARCH §27.5 host slice)`);
}

/** §27.5's "setblock ≤ SCR.MOB.SETBLOCK_MAX cells/dispatch", as a running total on
 *  the dispatch's own ctx (see `VerbTally`). The tally is created on first use so an
 *  omitted one cannot read as "unlimited". */
function chargeSetblock(ctx: VerbCtx, verb: string): void {
  const max = ctx.setblockMax ?? SCR.MOB.SETBLOCK_MAX;
  const tally = (ctx.tally ??= { setblocks: 0 });
  if (++tally.setblocks > max) {
    throw new ScriptError(`${verb}: a mob may set at most ${max} cells per dispatch (${tally.setblocks} requested)`);
  }
}

/** `self.moveTo` / `self.moveAway` (§27.6). Writes `Entity.wishX`/`wishZ` — the MOB-3
 *  steering intent — and NOTHING else; the goal is turned into a unit direction by the
 *  host's `pathToward` (so a script cannot walk through walls or off a cliff) and
 *  scaled by the mob's own `moveSpeed` (so it cannot outrun its `MobDef.speed`).
 *  Fleeing mirrors the goal through the mob's own position, exactly as MOB-3's
 *  `moveAway` (`sim/entities/archetypes/shared.ts`) does. */
function steer(ctx: VerbCtx, verb: string, args: Value[], away: boolean): Value {
  const mob = mobOf(ctx, verb);
  const to = point(args, verb);
  const goalX = away ? mob.x + (mob.x - to.x) : to.x;
  const goalZ = away ? mob.z + (mob.z - to.z) : to.z;
  const path = mob.pathToward(goalX, goalZ);
  const speed = mob.moveSpeed();
  mob.steering.wishX = path.dx * speed;
  mob.steering.wishZ = path.dz * speed;
  if (path.jump) mob.steering.wishJump = true;
  return true;
}

/** The yaw that points a mob at (x, z), in `Entity.yaw`'s convention — the same
 *  `atan2(-dx, -dz)` the entity system uses when it turns a mob toward its heading. */
function yawToward(mob: ScriptMobHost, x: number, z: number): number {
  return Math.atan2(-(x - mob.x), -(z - mob.z));
}

/** A point argument: one vec3, or three numbers. 2 arguments is neither — the
 *  {min,max} signature form cannot say "1 or 3", so the call reports it. */
function point(args: Value[], verb: string): Vec3 {
  if (args.length === 1) return vecArg(args[0], verb);
  if (args.length === 3) return vec3(num(args[0], verb), num(args[1], verb), num(args[2], verb));
  throw new ScriptError(`${verb}: expected a vec3 or x, y, z — got ${args.length} arguments`);
}

/** Identifier-pattern memory keys (§27.7). The same shape the lexer accepts for an
 *  identifier, so a key is always something an author could have typed as a name. */
const MEM_KEY = /^[A-Za-z_][A-Za-z0-9_]*$/;

function memKey(v: Value | undefined, verb: string): string {
  const key = str(v, verb);
  if (!MEM_KEY.test(key)) throw new ScriptError(`${verb}: '${key}' is not a valid memory key (letters, digits and _, not starting with a digit)`);
  return key;
}

/** `mem.set(key, value)` (§27.7). The value rules are enforced HERE — the one place
 *  that sees the script's value type — and the KEY-COUNT cap belongs to the store,
 *  which is the only side that knows what the entity already holds: a refusal comes
 *  back as `false` and aborts the dispatch with stored memory intact (E-43). */
function memSet(ctx: VerbCtx, verb: string, args: Value[]): Value {
  const mob = mobOf(ctx, verb);
  const key = memKey(args[0], verb);
  const value = val(args[1], verb);
  if (isVec3(value) || isList(value)) {
    throw new ScriptError(`${verb}: memory holds a number, string or bool — got a ${valueType(value)} (store a vec3 as three numbers)`);
  }
  if (typeof value === 'string' && value.length > SCR.MEM.STR_MAX) {
    throw new ScriptError(`${verb}: string of ${value.length} characters exceeds the limit of ${SCR.MEM.STR_MAX}`);
  }
  if (!mob.memSet(key, value)) {
    throw new ScriptError(`${verb}: memory is full (at most ${SCR.MEM.KEYS} keys)`);
  }
  return true;
}

/** An optional ScriptHost read, or an abort naming the verb. Absent ⇒ this host simply
 *  cannot answer, which is a refusal rather than a guess (D-TF-197). */
function hostRead<F>(fn: F | undefined, host: ScriptHost, verb: string): F {
  if (!fn) throw new ScriptError(`${verb}: this host provides no '${verb}'`);
  return (fn as F & { bind(h: ScriptHost): F }).bind(host);
}

/** The point a proximity read is measured FROM: the script's own position. These verbs
 *  take a radius/kind, never coordinates, so a script cannot probe the world remotely.
 *
 *  A MOB dispatch measures from the entity's LIVE pose (`ctx.mob`), not from a snapshot,
 *  because steering earlier in the same dispatch may already have moved it; a block
 *  dispatch measures from its cell (`ctx.self`). */
function selfPos(ctx: VerbCtx, verb: string): Vec3 {
  if (ctx.mob) return vec3(ctx.mob.x, ctx.mob.y, ctx.mob.z);
  if (ctx.self) return ctx.self;
  throw new ScriptError(`${verb}: no position for this dispatch`);
}

// ── Argument coercion (deterministic, total; a wrong type aborts the tick) ──────
function num(v: Value | undefined, verb: string): number {
  if (typeof v !== 'number') throw new ScriptError(`${verb}: expected a number, got ${v === undefined ? 'nothing' : valueType(v)}`);
  return v;
}
function int(v: Value | undefined, verb: string): number {
  return Math.floor(num(v, verb));
}
/** A health delta: a number, never negative — `self.hurt(-5)` must not be a heal. */
function amount(v: Value | undefined, verb: string): number {
  return Math.max(0, num(v, verb));
}
function str(v: Value | undefined, verb: string): string {
  if (typeof v !== 'string') throw new ScriptError(`${verb}: expected a string, got ${v === undefined ? 'nothing' : valueType(v)}`);
  return v;
}
function lst(v: Value | undefined, verb: string): ScriptList {
  if (v === undefined || !isList(v)) throw new ScriptError(`${verb}: expected a list, got ${v === undefined ? 'nothing' : valueType(v)}`);
  return v;
}
function vecArg(v: Value | undefined, verb: string): Vec3 {
  if (v === undefined || !isVec3(v)) throw new ScriptError(`${verb}: expected a vec3, got ${v === undefined ? 'nothing' : valueType(v)}`);
  return v;
}
/** Any value at all — only `push`'s element is unconstrained (lists are heterogeneous
 *  and may nest); a missing argument is still a type error, never a hole in a list. */
function val(v: Value | undefined, verb: string): Value {
  if (v === undefined) throw new ScriptError(`${verb}: expected a value, got nothing`);
  return v;
}
/** The one SCR.LIST_MAX gate (§27.2): over-long ⇒ ScriptError ⇒ this dispatch aborts
 *  (E-30). `list(…)` is also capped at compile time by its variadic signature. */
function bounded(items: readonly Value[], listMax: number): ScriptList {
  if (items.length > listMax) throw new ScriptError(`list length ${items.length} exceeds the limit of ${listMax}`);
  return list(items);
}
// Re-exported so the VM can surface a friendly type name in member-access errors.
export { isVec3, isList };
